/* eslint-disable sonarjs/no-duplicate-string */
import type { Request as ExpressRequest } from 'express';
import cookieUtils from '@zola-helpers/server/dist/es/utils/cookieUtils';
import fetch from 'isomorphic-fetch';

const Method = {
  get: 'GET',
  post: 'POST',
  put: 'PUT',
  delete: 'DELETE',
};

class ErrorWithResponse extends Error {
  response: unknown;
}

function filterUndefinedHeaders(headers: { [key: string]: string }): { [key: string]: string } {
  const finalHeaders: { [key: string]: string } = { 'Content-Type': 'application/json' };
  Object.keys(headers).forEach(key => {
    if (headers[key as keyof typeof headers] !== undefined) {
      finalHeaders[key] = headers[key as keyof typeof headers];
    }
  });
  return finalHeaders;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleErrors = ([ok, response]: [boolean, any]) => {
  if (!ok) {
    const errorMessage = (response.error && response.error.message) || 'An error has occurred';
    const error = new ErrorWithResponse(errorMessage);
    error.response = response;
    throw error;
  }
  return response;
};

const handlePromise = (response: Response) => {
  const contentType = response.headers && response.headers.get('Content-Type');
  if (contentType && contentType.indexOf('application/json') > -1) {
    return Promise.all([response.ok, response.json()]);
  }
  return Promise.all([response.ok, response.text()]);
};

const request = <T>(url: RequestInfo, options?: RequestInit): Promise<T> =>
  fetch(url, options).then(handlePromise).then(handleErrors);

const get = <T>(url: RequestInfo, options = {}, headers = {}): Promise<T> => {
  const finalHeaders = { 'Content-Type': 'application/json', ...headers };
  const finalOptions: RequestInit = {
    method: Method.get,
    credentials: 'same-origin',
    headers: finalHeaders,
    ...options,
  };

  return request(url, finalOptions);
};

const post = <T>(url: RequestInfo, body = {}, options = {}, headers = {}): Promise<T> => {
  const finalHeaders = filterUndefinedHeaders(headers); // filter all headers with value of undefined
  const finalBody =
    finalHeaders['Content-Type'] === 'application/json' ? JSON.stringify(body) : body;
  const finalOptions: RequestInit = {
    method: Method.post,
    credentials: 'same-origin',
    body: finalBody as string,
    headers: finalHeaders,
    ...options,
  };

  return request(url, finalOptions);
};

const deleteMethod = <T>(url: RequestInfo, options = {}, headers = {}): Promise<T> => {
  const finalHeaders = { 'Content-Type': 'application/json', ...headers };
  const finalOptions: RequestInit = {
    method: Method.delete,
    credentials: 'same-origin',
    headers: finalHeaders,
    ...options,
  };

  return request(url, finalOptions);
};

const put = <T>(url: RequestInfo, body = {}, options = {}, headers = {}): Promise<T> => {
  const finalHeaders = filterUndefinedHeaders(headers); // filter all headers with value of undefined
  const finalBody =
    finalHeaders['Content-Type'] === 'application/json' ? JSON.stringify(body) : body;
  const finalOptions: RequestInit = {
    method: Method.put,
    credentials: 'same-origin',
    body: finalBody as string,
    headers: finalHeaders,
    ...options,
  };

  return request(url, finalOptions);
};

// JASON TODO: Move cookie functions and this entire file to zola-helpers
const getCookie = (cookieName: string, req: ExpressRequest) => {
  if (!req.cookies) return undefined;
  return req.cookies[cookieName];
};

const getCookieHeader = (req: ExpressRequest) => {
  const authCookieNames = [
    cookieUtils.getLoginCookieName(),
    cookieUtils.getUserSessionCookieName(),
    cookieUtils.getUserSessionRefreshCookieName(),
  ];

  const finalString = authCookieNames.reduce((acc, cookieName) => {
    const cookieValue = getCookie(cookieName, req);
    if (cookieValue) {
      return `${acc}${cookieName}=${cookieValue}; `;
    }
    return acc;
  }, '');

  return { Cookie: finalString };
};

const ApiService = {
  request,
  get,
  put,
  post,
  delete: deleteMethod,
  getCookieHeader,
};

export default ApiService;
